import { useQuery } from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useEffect, useMemo } from "react";
import { QueryKeys, QueryTimes } from "~/application/constants";
import { createTheme } from "~/application/theme";
import { ThemeData } from "~/application/types/entities/ThemeData.type";
import { themeService } from "~/application/usecases/Theme";
import { AppLoading } from "~/containers/AppLoading";


const ThemeContext = createContext<ThemeData>({} as ThemeData);

export const useTheme = () => useContext(ThemeContext);

interface DynamicThemeProviderProps {
  children: ReactNode;
}

export const DynamicThemeProvider = ({ children }: DynamicThemeProviderProps) => {
  const { data: themeData, isLoading } = useQuery(
    [QueryKeys.THEME_DATA],
    () => themeService.getThemeData(),
    {
      staleTime: QueryTimes.LONGEST,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error("Using default theme due to error:", error);
      },
    }
  );

  const themeClassName = useMemo(() => {
    if (!themeData) return "";
    try {
      const dynamicTheme = createTheme({
        colors: {
          ...themeData.colors,
        },
      });
      return dynamicTheme.className;
    } catch (error) {
      console.error("Failed to create dynamic theme:", error);
      return "";
    }
  }, [themeData]);

  useEffect(() => {
    const body = document.body;

    if (themeClassName) {
      body.classList.add(themeClassName);
    }
    if (themeData?.colors?.["neutrals-lightest"]) {
      body.style.backgroundColor = themeData.colors["neutrals-lightest"];
    }

    return () => {
      if (themeClassName) {
        body.classList.remove(themeClassName);
      }
      body.style.backgroundColor = "";
    };
  }, [themeClassName, themeData]);


  if (isLoading) {  
    return <AppLoading />;
  }

  return (
    <ThemeContext.Provider value={themeData!}>
      {children}
    </ThemeContext.Provider>
  );
};
